import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { UnifiedModulePaymentCheckout } from './payment/UnifiedModulePaymentCheckout';
import { useTenant } from '../context/TenantContext';
import { BookingService, UIIntent } from '../services/bookingService';
import { ServiceSelection } from './booking/ServiceSelection';
import { ResourceSelection } from './booking/ResourceSelection';
import { SlotSelection } from './booking/SlotSelection';
import { DateSlotSelection } from './booking/DateSlotSelection';
import type { DateOption, ResourceOption, ServiceSummary } from './booking/DateSlotSelection';
import './booking/booking-brand.css';
import { BookingReview } from './booking/BookingReview';
import { StepTransition } from './booking/StepTransition';
import CartReview from './booking/CartReview';
import CartBottomBar from './booking/CartBottomBar';
import type { CartSummaryVM } from './booking/CartReview';
import { IdentityForm } from './booking/IdentityForm';
import { BundlePurchase } from './booking/BundlePurchase';
import { BookingConfirmation } from './booking/BookingConfirmation';
import type { ConfirmedLineVM } from './booking/BookingConfirmation';
import './BookingPage.css';
import { BookingFlowHeader } from './BookingFlowHeader';
import { getOrCreateWebPaymentGuestId, getWebBookingGuestEmail, setWebBookingGuestEmail, webPaymentGuestStorageKey, setWebBookingWaId } from '../utils/webPaymentGuest';
import { BookingChromeActions } from './booking/BookingChromeActions';
import { BookingGuestContactModal } from './booking/BookingGuestContactModal';
import { RestaurantLiveBooking } from './booking/RestaurantLiveBooking';
import { stripEmojiUnicode } from '../utils/stripEmoji';
import { localeTagFromLang } from '../utils/bookingLocaleDisplay';
import { FlowToastGlyph } from './FlowToastGlyph';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Acción "back" por paso: el backend ya tiene back_to_services, back_to_dates, back_to_resources, back_to_times
const STEP_TO_BACK_ACTION: Record<string, string> = {
  SelectSlot: 'back_to_services', // date strip is embedded in the slot step now
  SelectResource: 'back_to_times',
  SelectDate: 'back_to_services',
  SelectService: '',
  BookingReview: 'back_to_times',
  RequestIdentity: 'back_to_times',
  ShowList: 'start',
  ShowMenu: 'start',
};

import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { logger } from '@core/logger';
import { getPaymentGatewayBrowserBase, getPaymentGatewayPublicUrl } from '../services/paymentGatewayBrowserBase';
import { usePaymentReturnSession } from '../hooks/usePaymentReturnSession';
import { PaymentIntentResultPanel } from './payment/PaymentIntentResultPanel';
import { WebpaymentConfirmation } from './shared/WebpaymentConfirmation';
import { useWebBookingBranding } from '../context/WebBookingBrandingContext';
import { ChevronLeft, Lock } from 'lucide-react';
import { resolveBookingErrorMessage, normalizeBackendErrorMessage } from '../utils/bookingErrorMessage';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * Payload de contacto de quien ha iniciado sesión con Clerk, para que el backend actualice el
 * end_user (consola Users) y pueda crear la reserva. Va también el NOMBRE: sin él la reserva nacía
 * sin `customer_name` y, si el token Clerk no se puede verificar en el orquestador, el cliente
 * quedaba bloqueado en "Revisa y confirma" sin ningún sitio donde escribirlo.
 */
function getClerkContactPayload(
  user:
    | {
        primaryEmailAddress?: { emailAddress?: string } | null;
        fullName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
      }
    | null,
): Record<string, unknown> {
  const email = user?.primaryEmailAddress?.emailAddress?.trim() ?? '';
  const hasEmail = email.includes('@');
  const name = (
    user?.fullName?.trim() || [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  ).substring(0, 255);
  if (!hasEmail && !name) return {};
  const contactInfo = { ...(hasEmail ? { email } : {}), ...(name ? { name } : {}) };
  return { ...(hasEmail ? { email } : {}), ...(name ? { name } : {}), contactInfo };
}

/** Solo se renderiza cuando Clerk está configurado; así los hooks se llaman siempre dentro de ClerkProvider. */
function BookingPageWithClerk() {
  const clerkAuth = useAuth();
  const clerkUser = useUser();
  const { signOut } = useClerk();
  return (
    <BookingPageInner
      getToken={clerkAuth?.getToken ?? (async () => null)}
      isSignedIn={clerkAuth?.isSignedIn ?? false}
      user={clerkUser?.user ?? null}
      signOut={
        signOut
          ? async () => {
              await signOut({
                redirectUrl:
                  typeof window !== 'undefined'
                    ? `${window.location.origin}${window.location.pathname}${window.location.search}`
                    : '/booking',
              });
            }
          : undefined
      }
    />
  );
}

export function BookingPage() {
  if (CLERK_PUBLISHABLE_KEY) {
    return <BookingPageWithClerk />;
  }
  return (
    <BookingPageInner
      getToken={async () => null}
      isSignedIn={false}
      user={null}
      signOut={undefined}
    />
  );
}

/** "45 min · 40 €" para la cabecera contextual del paso 2. */
function serviceMetaLabel(
  svc: { duration?: string | number; price?: string | number; currency?: string } | null,
  lang: string,
): string {
  if (!svc) return '';
  const parts: string[] = [];
  if (typeof svc.duration === 'number') parts.push(`${svc.duration} min`);
  else if (typeof svc.duration === 'string') {
    const [h, m] = svc.duration.split(':').map(Number);
    const total = (h || 0) * 60 + (m || 0);
    if (total > 0) parts.push(`${total} min`);
  }
  const price = typeof svc.price === 'string' ? parseFloat(svc.price) : svc.price;
  if (typeof price === 'number' && Number.isFinite(price) && price > 0) {
    parts.push(formatCurrency(price, svc.currency, lang));
  }
  return parts.join(' · ');
}

/** Importe ya en unidades (no céntimos) con la moneda del tenant. */
function formatCurrency(amount: number, currency?: string, lang?: string): string {
  const curr = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(localeTagFromLang(lang || 'es'), {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${curr}`;
  }
}

function extractEmailFromPayload(p: unknown): string | null {
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  const fromContact = o.contactInfo && typeof o.contactInfo === 'object' && (o.contactInfo as { email?: string }).email;
  const raw = (o.email ?? fromContact ?? '').toString().trim().toLowerCase();
  return raw.includes('@') ? raw : null;
}

/** Dedicated form for restaurant booking details (party size + optional zone). */
function RestaurantDetailsForm({
  title,
  message,
  fields,
  minPartySize,
  maxPartySize,
  submitLabel,
  onSubmit,
}: {
  title?: string;
  message?: string;
  fields: Array<{ id: string; label: string; type?: string; required?: boolean; placeholder?: string; options?: Array<{ value: string; label: string }> }>;
  minPartySize: number;
  maxPartySize: number;
  submitLabel: string;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const partySizeField = fields.find((f) => f.id === 'party_size');
  const zoneField = fields.find((f) => f.id === 'preferred_zone');
  const otherFields = fields.filter((f) => f.id !== 'party_size' && f.id !== 'preferred_zone');

  const [partySize, setPartySize] = useState<number>(minPartySize);
  const [zone, setZone] = useState<string>('');

  const decrement = () => setPartySize((v) => Math.max(minPartySize, v - 1));
  const increment = () => setPartySize((v) => Math.min(maxPartySize, v + 1));
  const [otherValues, setOtherValues] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {};
    if (partySizeField) payload.party_size = partySize;
    if (zoneField && zone) payload.preferred_zone = zone;
    Object.entries(otherValues).forEach(([k, v]) => { if (v) payload[k] = v; });
    onSubmit(payload);
  };

  return (
    <form className="booking-step identity-form restaurant-details-form" onSubmit={handleSubmit}>
      <h2>{stripEmojiUnicode(title?.trim() || 'Detalles de la reserva')}</h2>
      {message ? <p className="identity-message">{stripEmojiUnicode(message)}</p> : null}

      {partySizeField ? (
        <div className="form-field">
          <label>
            {stripEmojiUnicode(partySizeField.label)}
            {partySizeField.required ? <span className="required"> *</span> : null}
          </label>
          <div className="party-size-stepper">
            <button type="button" className="stepper-btn" onClick={decrement} aria-label="Menos" disabled={partySize <= minPartySize}>−</button>
            <span className="stepper-count">{partySize}</span>
            <button type="button" className="stepper-btn" onClick={increment} aria-label="Más" disabled={partySize >= maxPartySize}>+</button>
          </div>
          <p className="stepper-hint">{minPartySize}–{maxPartySize} personas</p>
        </div>
      ) : null}

      {zoneField ? (
        <div className="form-field">
          <label htmlFor="preferred_zone">
            {stripEmojiUnicode(zoneField.label)}
            {zoneField.required ? <span className="required"> *</span> : null}
          </label>
          {Array.isArray(zoneField.options) && zoneField.options.length > 0 ? (
            <select
              id="preferred_zone"
              className="zone-select"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              required={Boolean(zoneField.required)}
            >
              <option value="">Sin preferencia</option>
              {zoneField.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {stripEmojiUnicode(opt.label)}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="preferred_zone"
              type="text"
              className="zone-input"
              value={zone}
              placeholder={zoneField.placeholder || ''}
              required={Boolean(zoneField.required)}
              onChange={(e) => setZone(e.target.value)}
            />
          )}
        </div>
      ) : null}

      {otherFields.map((f) => (
        <div key={f.id} className="form-field">
          <label htmlFor={f.id}>
            {stripEmojiUnicode(f.label)}
            {f.required ? <span className="required"> *</span> : null}
          </label>
          <input
            id={f.id}
            type={f.type === 'number' ? 'number' : 'text'}
            required={Boolean(f.required)}
            placeholder={f.placeholder}
            value={otherValues[f.id] ?? ''}
            onChange={(e) => setOtherValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
          />
        </div>
      ))}

      <div className="booking-actions">
        <button type="submit" className="btn-primary btn-confirm">
          {stripEmojiUnicode(submitLabel)}
        </button>
      </div>
    </form>
  );
}

function BookingPageInner({
  getToken,
  isSignedIn,
  user,
  signOut,
}: {
  getToken: (opts?: { template?: string }) => Promise<string | null>;
  isSignedIn: boolean;
  user:
    | {
        id: string;
        primaryEmailAddress?: { emailAddress?: string } | null;
        fullName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
      }
    | null;
  signOut?: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/restaurant') ? '/restaurant' : '/booking';
  const catalogScope: 'booking' | 'restaurant' = basePath === '/restaurant' ? 'restaurant' : 'booking';
  const tenantFromHost = useTenant();
  const { profileSlug } = useWebBookingBranding();
  const { user: telegramUser, isTelegramWebApp, initData: telegramInitDataRaw } = useTelegramWebApp();
  const isBackNavigationRef = useRef(false);
  const currentStepRef = useRef<string | null>(null);
  const rescheduleDeepLinkDoneRef = useRef(false);
  const initializingRef = useRef(false);

  const effectiveTenantId =
    tenantFromHost.tenantId ??
    searchParams.get('tenant_id') ??
    searchParams.get('tenantId') ??
    'default-tenant';

  const [tenantId, setTenantId] = useState<string>(effectiveTenantId);

  const telegramId =
    searchParams.get('telegram_id') ||
    (telegramUser?.id != null ? String(telegramUser.id) : null);
  const whatsappId = searchParams.get('whatsapp_id');
  // Persist WA ID so the business landing can show booking history
  if (whatsappId && effectiveTenantId) setWebBookingWaId(effectiveTenantId, whatsappId);

  const botIdFromUrl =
    searchParams.get('bot_id') || searchParams.get('botId') || undefined;

  const waBookingTokenStorageKey = (tid: string) => `web_payment_wa_booking_token:${tid}`;

  const [guestEmailLocal, setGuestEmailLocal] = useState<string | null>(() =>
    getWebBookingGuestEmail(effectiveTenantId),
  );
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [activityUnreadCount, setActivityUnreadCount] = useState(0);

  useEffect(() => {
    setGuestEmailLocal(getWebBookingGuestEmail(effectiveTenantId));
  }, [effectiveTenantId]);

  const emailForBookings =
    searchParams.get('email')?.trim().toLowerCase() ||
    user?.primaryEmailAddress?.emailAddress?.trim().toLowerCase() ||
    guestEmailLocal ||
    null;

  /**
   * Lo que ya sabemos de quien ha iniciado sesión. Solo se usa para prerrellenar el formulario de
   * contacto cuando hay que volver a pedirlo: nadie debería reescribir su nombre y su email si ya
   * los tenemos.
   */
  const clerkContactDefaults = useMemo(
    () => ({
      name:
        user?.fullName?.trim() ||
        [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
        '',
      email: emailForBookings ?? '',
    }),
    [user?.fullName, user?.firstName, user?.lastName, emailForBookings],
  );

  useEffect(() => {
    setTenantId(effectiveTenantId);
  }, [effectiveTenantId]);

  const getWaBookingToken = useCallback((): string | undefined => {
    if (!tenantId || tenantId === 'default-tenant') return undefined;
    try {
      const stored =
        typeof window !== 'undefined' ? sessionStorage.getItem(waBookingTokenStorageKey(tenantId)) : null;
      if (stored?.trim()) return stored.trim();
    } catch {
      // ignore
    }
    return searchParams.get('wa_booking_token')?.trim() || undefined;
  }, [tenantId, searchParams]);
  const waBookingToken = getWaBookingToken();
  const isChannelIdentityFlow = Boolean(telegramId || whatsappId || waBookingToken || isTelegramWebApp);

  useEffect(() => {
    const tid = effectiveTenantId;
    const raw = searchParams.get('wa_booking_token')?.trim();
    if (!tid || tid === 'default-tenant' || !raw) return;
    try {
      sessionStorage.setItem(waBookingTokenStorageKey(tid), raw);
    } catch {
      // ignore
    }
    const next = new URLSearchParams(searchParams);
    next.delete('wa_booking_token');
    setSearchParams(next, { replace: true });
  }, [effectiveTenantId, searchParams, setSearchParams]);

  // Persist sessionId + uiIntent so a page refresh restores the current step (especially ShowForm)
  const SESSION_KEY = (tid: string) => `bk_sid:${tid}`;
  const SESSION_TS_KEY = (tid: string) => `bk_sid_ts:${tid}`;
  const SESSION_UI_KEY = (tid: string) => `bk_ui:${tid}`;
  const SESSION_MAX_AGE_MS = 28 * 60 * 1000; // 28 min (Redis hold TTL is 30 min)

  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const tid = effectiveTenantId;
      if (!tid || tid === 'default-tenant') return null;
      const ts = Number(sessionStorage.getItem(SESSION_TS_KEY(tid)) ?? '0');
      if (Date.now() - ts > SESSION_MAX_AGE_MS) return null;
      return sessionStorage.getItem(SESSION_KEY(tid)) || null;
    } catch { return null; }
  });
  // Sync sessionId to sessionStorage so refresh can restore it
  useEffect(() => {
    if (!sessionId || !tenantId || tenantId === 'default-tenant') return;
    try {
      sessionStorage.setItem(SESSION_KEY(tenantId), sessionId);
      sessionStorage.setItem(SESSION_TS_KEY(tenantId), String(Date.now()));
    } catch { /* ignore */ }
  }, [sessionId, tenantId]);

  const [uiIntent, setUiIntent] = useState<UIIntent | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const tid = effectiveTenantId;
      if (!tid || tid === 'default-tenant') return null;
      const ts = Number(sessionStorage.getItem(SESSION_TS_KEY(tid)) ?? '0');
      if (Date.now() - ts > SESSION_MAX_AGE_MS) return null;
      const raw = sessionStorage.getItem(SESSION_UI_KEY(tid));
      return raw ? (JSON.parse(raw) as UIIntent) : null;
    } catch { return null; }
  });
  currentStepRef.current = uiIntent?.type ?? null;

  // Persist uiIntent to sessionStorage so refresh can restore it
  useEffect(() => {
    if (!tenantId || tenantId === 'default-tenant') return;
    try {
      if (uiIntent) {
        sessionStorage.setItem(SESSION_UI_KEY(tenantId), JSON.stringify(uiIntent));
      } else {
        sessionStorage.removeItem(SESSION_UI_KEY(tenantId));
      }
    } catch { /* ignore */ }
  }, [uiIntent, tenantId]);
  const handleActionRef = useRef<(action: string, payload: any) => Promise<void>>(() => Promise.resolve());

  // Persist calendar state across SelectDate → SelectSlot transitions for combined view
  const calendarDatesRef = useRef<DateOption[]>([]);
  const calendarSelectedDateRef = useRef<string | null>(null);
  const calendarServiceRef = useRef<ServiceSummary | null>(null);
  // Chips de "¿con quién?" del paso 2. El backend solo los envía junto a los huecos (SelectSlot),
  // así que se conservan aquí para que sigan visibles al cambiar de día o al quedarse sin horas.
  const calendarResourcesRef = useRef<ResourceOption[]>([]);
  const calendarResourceIdRef = useRef<string | null>(null);
  // Días (YYYY-MM-DD) que se han descubierto SIN horarios: se pintan en gris de forma estable.
  // Se rellena en handleAction (determinista, no en render) cuando select_date de esa fecha
  // devuelve ShowError, y se resetea cuando llega una lista de fechas nueva (SelectDate).
  const noSlotDatesRef = useRef<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setCurrentStep] = useState<string>('start');
  // Espejo local del carrito (fuente reactiva para la barra sticky). Se actualiza con cada
  // respuesta que trae data.cart y se persiste para sobrevivir navegación/recarga.
  const [cartSummary, setCartSummary] = useState<CartSummaryVM | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem(`wp_cart_${effectiveTenantId}`);
      return raw ? (JSON.parse(raw) as CartSummaryVM) : null;
    } catch {
      return null;
    }
  });
  const applyCartFromResponse = (intent: { data?: any } | null | undefined) => {
    const c = (intent?.data as any)?.cart as CartSummaryVM | undefined;
    if (!c) return;
    setCartSummary(c);
    try {
      if ((c.items?.length ?? 0) > 0) {
        sessionStorage.setItem(`wp_cart_${effectiveTenantId}`, JSON.stringify(c));
      } else {
        sessionStorage.removeItem(`wp_cart_${effectiveTenantId}`);
      }
    } catch {
      /* ignore */
    }
  };
  // Motivo por el que el servidor rechazó confirmar el carrito (se muestra en CartReview).
  const [cartConfirmError, setCartConfirmError] = useState<string | null>(null);
  /**
   * El servidor ha rechazado la confirmación por faltar nombre/contacto (code 'missing_contact').
   * Ocurre aunque el cliente esté identificado: si el orquestador no puede verificar su token Clerk
   * lo trata como invitado. Con esto se le vuelven a pedir los datos en vez de dejarle atascado.
   */
  const [cartContactRequired, setCartContactRequired] = useState(false);
  // Vista del flujo de reserva: 'draft' = pantalla "Tu reserva" (edición); 'flow' = sub-flujo de
  // selección (servicio→fecha→hora). El carrito es la reserva viva; los pasos la alimentan.
  const [viewMode, setViewMode] = useState<'draft' | 'flow'>(() => {
    const hasItems = (() => {
      if (typeof window === 'undefined') return false;
      try {
        const raw = sessionStorage.getItem(`wp_cart_${effectiveTenantId}`);
        const c = raw ? (JSON.parse(raw) as CartSummaryVM) : null;
        return (c?.items?.length ?? 0) > 0;
      } catch {
        return false;
      }
    })();
    return hasItems ? 'draft' : 'flow';
  });
  // Índice de la línea que se está reeditando (null = añadiendo nueva). En edición, add_service
  // sustituye esa línea (replaceIndex) en vez de añadir una nueva.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>(() => {
    const stored = localStorage.getItem('booking_language');
    if (stored) return stored;
    const nav = typeof navigator !== 'undefined' ? navigator.language : '';
    if (nav?.startsWith('en')) return 'en';
    if (nav?.startsWith('fr')) return 'fr';
    if (nav?.startsWith('de')) return 'de';
    return 'es';
  });

  const { session: paymentReturn, clearSession } = usePaymentReturnSession(effectiveTenantId);

  useEffect(() => {
    if (!paymentReturn?.langFromReturn) return;
    const lng = paymentReturn.langFromReturn;
    if (['es', 'en', 'fr', 'de'].includes(lng)) {
      setLanguage(lng);
      i18n.changeLanguage(lng);
      try {
        localStorage.setItem('booking_language', lng);
      } catch {
        // ignore
      }
    }
  }, [paymentReturn?.langFromReturn, i18n]);

  // Clave Stripe: env o la obtenida del backend (GET /config/stripe-publishable-key) para Embedded Checkout
  const [stripePublishableKeyFromApi, setStripePublishableKeyFromApi] = useState<string>('');
  const [stripeKeyLoading, setStripeKeyLoading] = useState(false);
  /** Red/CORS/HTTP/no JSON — no confundir con 200 y cuerpo sin pk */
  const [stripeKeyFetchFailed, setStripeKeyFetchFailed] = useState(false);
  const [stripeKeyEmptyFromApi, setStripeKeyEmptyFromApi] = useState(false);
  /** Aviso fijo inferior (mismo patrón visual que Gym: `scheduleFlash` o pasos finales). */
  const [flowFlashLocal, setFlowFlashLocal] = useState<{
    kind: 'success' | 'info' | 'error';
    message: string;
  } | null>(null);
  const effectiveStripeKey = STRIPE_PUBLISHABLE_KEY || stripePublishableKeyFromApi;
  const stripePromise = useMemo(
    () => (effectiveStripeKey ? loadStripe(effectiveStripeKey) : null),
    [effectiveStripeKey],
  );

  // Obtener publishable key del backend cuando hace falta para EmbeddedCheckout (evita depender de .env)
  useEffect(() => {
    if (
      uiIntent?.type !== 'EmbeddedCheckout' ||
      STRIPE_PUBLISHABLE_KEY ||
      stripePublishableKeyFromApi ||
      stripeKeyFetchFailed ||
      stripeKeyEmptyFromApi ||
      !getPaymentGatewayBrowserBase() ||
      !tenantId
    ) {
      return;
    }
    let cancelled = false;
    setStripeKeyFetchFailed(false);
    setStripeKeyEmptyFromApi(false);
    setStripeKeyLoading(true);
    const url = `${getPaymentGatewayBrowserBase()}/config/stripe-publishable-key?tenant_id=${encodeURIComponent(tenantId)}`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(`stripe-publishable-key HTTP ${r.status}`);
        }
        return r.json() as Promise<{ stripePublishableKey?: string }>;
      })
      .then((data) => {
        if (cancelled) return;
        const key = (data?.stripePublishableKey || '').trim();
        if (key) setStripePublishableKeyFromApi(key);
        else setStripeKeyEmptyFromApi(true);
      })
      .catch(() => {
        if (!cancelled) setStripeKeyFetchFailed(true);
      })
      .finally(() => {
        if (!cancelled) setStripeKeyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uiIntent?.type, tenantId, STRIPE_PUBLISHABLE_KEY, stripePublishableKeyFromApi, stripeKeyFetchFailed, stripeKeyEmptyFromApi]);

  // ✅ Obtener token de Clerk si el usuario está autenticado
  useEffect(() => {
    const getClerkToken = async () => {
      if (isSignedIn && user) {
        try {
          const token = await getToken({ template: 'default-jwt' });
          setAuthToken(token || null);
          setUserId(user.id);
        } catch (err) {
          logger.error('Error obteniendo token de Clerk:', err);
        }
      } else {
        setAuthToken(null);
        setUserId(null);
      }
    };
    getClerkToken();
  }, [isSignedIn, user, getToken]);

  // ✅ Restaurar estado de sesión después de login con Clerk
  useEffect(() => {
    const savedSessionId = localStorage.getItem('booking_sessionId');
    const savedTenantId = localStorage.getItem('booking_tenantId');
    const savedAction = localStorage.getItem('booking_pendingAction');
    
    // ✅ Solo restaurar si el usuario acaba de hacer login Y hay estado guardado
    if (savedSessionId && savedTenantId && savedAction && isSignedIn && user && !sessionId) {
      logger.debug('🔄 Restaurando estado después de login:', { savedSessionId, savedTenantId, savedAction });
      
      // Usuario acaba de hacer login, restaurar estado y continuar con la acción pendiente
      setSessionId(savedSessionId);
      if (savedTenantId !== tenantId) {
        setTenantId(savedTenantId);
      }
      
      // Limpiar estado guardado
      localStorage.removeItem('booking_sessionId');
      localStorage.removeItem('booking_tenantId');
      localStorage.removeItem('booking_pendingAction');
      
      // Continuar con la acción pendiente (confirm)
      if (savedAction === 'confirm') {
        // Obtener el payload guardado si existe
        const savedPayload = localStorage.getItem('booking_pendingPayload');
        const payload = savedPayload ? JSON.parse(savedPayload) : {};
        localStorage.removeItem('booking_pendingPayload');
        
        // Esperar un momento para que el token esté disponible, hacer merge invitado→Clerk y luego continuar
        const continueAfterLogin = async () => {
          let token: string | null = null;
          if (getToken) {
            try {
              token = await getToken({ template: 'default-jwt' });
              setAuthToken(token || null);
              setUserId(user.id);
            } catch (err) {
              logger.error('❌ Error obteniendo token de Clerk:', err);
            }
          }
          // Merge invitado → usuario Clerk (misma identidad para la confirmación)
          const guestIdForMerge =
            typeof window !== 'undefined' ? localStorage.getItem(webPaymentGuestStorageKey(savedTenantId)) : null;
          if (guestIdForMerge && token && user?.id) {
            try {
              await BookingService.mergeGuestWithUser(savedTenantId, guestIdForMerge, token, user.id);
            } catch (err) {
              logger.warn('Merge invitado→Clerk falló (se continúa con confirm):', err);
            }
          }
          setTimeout(() => {
            handleAction('confirm', payload);
          }, 500);
        };

        continueAfterLogin();
        return;
      }
    }
    
    // Rehidratar estado si falta uiIntent aunque exista sessionId restaurado.
    // Esto evita quedarse en loading infinito tras refresh con storage parcial.
    if (!savedAction && !tenantFromHost.loading && (!sessionId || !uiIntent)) {
      initializeBooking();
    }
  }, [tenantId, isSignedIn, user, sessionId, uiIntent, tenantFromHost.loading]);

  // When session was restored from sessionStorage, stop the loading spinner immediately
  useEffect(() => {
    if (sessionId && uiIntent) {
      setLoading(false);
    }
  }, [sessionId, uiIntent]);

  const initializeBooking = async (overrideLanguage?: string) => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    const langToUse = overrideLanguage ?? language;
    // Clear stored state only when starting a brand-new session.
    // If we are restoring with an existing sessionId, keep storage intact.
    if (!sessionId) {
      try {
        if (tenantId && tenantId !== 'default-tenant') {
          sessionStorage.removeItem(SESSION_KEY(tenantId));
          sessionStorage.removeItem(SESSION_TS_KEY(tenantId));
          sessionStorage.removeItem(SESSION_UI_KEY(tenantId));
        }
      } catch {
        /* ignore */
      }
    }
    try {
      setLoading(true);
      setError(null);
      if (catalogScope === 'restaurant' && (!tenantId || tenantId === 'default-tenant')) {
        throw new Error('tenant_id requerido para reservas de restaurante');
      }

      // Mini App / enlace WhatsApp firmado: identidad en backend; no forzar guest_* como identidad principal.
      const waTok = getWaBookingToken();
      const guestId =
        !isTelegramWebApp && !waTok ? getOrCreateWebPaymentGuestId(tenantId) : undefined;
      const response = await BookingService.startBooking(
        tenantId,
        sessionId || undefined,
        langToUse,
        userId ?? undefined,
        guestId,
        telegramInitDataRaw || undefined,
        botIdFromUrl,
        waTok,
        catalogScope,
      );
      
      // Verificar que la respuesta tiene los campos necesarios
      if (!response.sessionId) {
        throw new Error('No se recibió sessionId en la respuesta del servidor');
      }
      
      if (!response.uiIntent) {
        throw new Error('No se recibió uiIntent en la respuesta del servidor');
      }
      
      setSessionId(response.sessionId);
      setUiIntent(response.uiIntent);
      setCurrentStep(response.uiIntent.type);
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(location.search);
        params.set('step', response.uiIntent.type);
        window.history.replaceState({ step: response.uiIntent.type }, '', `${location.pathname}?${params}`);
      }
    } catch (err: any) {
      // If restored session is stale/corrupt, clear storage and retry once as fresh flow.
      const hadRestoredSession = Boolean(sessionId);
      if (hadRestoredSession && tenantId && tenantId !== 'default-tenant') {
        try {
          sessionStorage.removeItem(SESSION_KEY(tenantId));
          sessionStorage.removeItem(SESSION_TS_KEY(tenantId));
          sessionStorage.removeItem(SESSION_UI_KEY(tenantId));
        } catch {
          // ignore
        }
        setSessionId(null);
        try {
          const waTok = getWaBookingToken();
          const guestId =
            !isTelegramWebApp && !waTok ? getOrCreateWebPaymentGuestId(tenantId) : undefined;
          const retry = await BookingService.startBooking(
            tenantId,
            undefined,
            langToUse,
            userId ?? undefined,
            guestId,
            telegramInitDataRaw || undefined,
            botIdFromUrl,
            waTok,
            catalogScope,
          );
          if (retry?.sessionId && retry?.uiIntent) {
            setSessionId(retry.sessionId);
            setUiIntent(retry.uiIntent);
            setCurrentStep(retry.uiIntent.type);
            return;
          }
        } catch {
          // fall through to error below
        }
      }
      setError(resolveBookingErrorMessage(err, tLng));
    } finally {
      setLoading(false);
      initializingRef.current = false;
    }
  };

  const handleAction = async (action: string, payload: any) => {
    if (!sessionId) {
      setError('Session ID no encontrado');
      return;
    }

    // En restaurante live flow, algunos cambios de filtros (personas/zona/fecha)
    // deben ser "soft" para evitar parpadeo de pantalla completa.
    const softActions = new Set(['select_restaurant_details', 'select_date']);
    const useGlobalLoading = !(catalogScope === 'restaurant' && softActions.has(action));

    try {
      if (useGlobalLoading) {
        setLoading(true);
      }
      setError(null);

      // ✅ Logging para debug
      logger.debug('📤 BookingPage - handleAction:', {
        action,
        hasPayload: !!payload,
        payloadKeys: payload ? Object.keys(payload) : [],
        hasAuthToken: !!authToken,
        hasUserId: !!userId,
        sessionId,
        tenantId,
      });

      const waTok = getWaBookingToken();
      const guestId =
        !isTelegramWebApp && !waTok ? getOrCreateWebPaymentGuestId(tenantId) : undefined;
      // Incluir email de Clerk en el payload para que el backend guarde el contacto del end_user (consola)
      const contactPayload = isSignedIn && user ? getClerkContactPayload(user) : {};
      const channelIdentity: Record<string, unknown> = {
        ...(telegramId ? { telegram_id: telegramId } : {}),
        ...(emailForBookings ? { email: emailForBookings } : {}),
      };
      const enrichedPayload = { ...channelIdentity, ...contactPayload, ...(payload || {}) };
      const guestEmail = extractEmailFromPayload(enrichedPayload);
      if (guestEmail) {
        setWebBookingGuestEmail(tenantId, guestEmail);
        setGuestEmailLocal(guestEmail);
      }
      const response = await BookingService.handleAction(
        tenantId,
        sessionId,
        action,
        enrichedPayload,
        authToken || undefined,
        userId || undefined,
        language,
        guestId,
        telegramInitDataRaw || undefined,
        botIdFromUrl,
        waTok,
        catalogScope,
      );
      
      logger.debug('📥 BookingPage - Respuesta recibida:', {
        hasSessionId: !!response.sessionId,
        hasUiIntent: !!response.uiIntent,
        uiIntentType: response.uiIntent?.type,
        status: response.status,
      });
      
      // Actualizar sessionId si viene en la respuesta
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }
      
      // Verificar que uiIntent existe
      if (!response.uiIntent) {
        throw new Error('No se recibió uiIntent en la respuesta del servidor');
      }
      
      setUiIntent(response.uiIntent);
      setCurrentStep(response.uiIntent.type);
      applyCartFromResponse(response.uiIntent);
      // Días sin horarios (para pintarlos en gris de forma estable). Una lista de fechas nueva
      // resetea el registro; un select_date de una fecha concreta que devuelve ShowError marca
      // SOLO esa fecha (determinista, nunca la fecha a la que se está navegando).
      if (response.uiIntent.type === 'SelectDate' || action === 'set_resource_filter') {
        /*
         * Cambiar de profesional invalida el registro: "sin horas" solo era cierto para el
         * filtro anterior. Antes no se limpiaba nunca aquí —`set_resource_filter` devuelve
         * SelectSlot o ShowError, jamás SelectDate—, así que los días que se habían puesto en
         * gris con un profesional seguían grises y CLICABLES a medias con otro, y solo se
         * recuperaban volviendo al catálogo de servicios. Es el "me bloquea las fechas".
         */
        noSlotDatesRef.current = new Set();
      } else if (
        response.uiIntent.type === 'ShowError' &&
        action === 'select_date' &&
        typeof payload?.date === 'string'
      ) {
        noSlotDatesRef.current.add(payload.date.slice(0, 10));
      }
      // Transiciones draft/flow: las acciones de carrito llevan a "Tu reserva"; iniciar una
      // selección entra en el sub-flujo.
      if (action === 'add_service' || action === 'remove_service' || action === 'view_cart') {
        setViewMode('draft');
        if (action === 'add_service') setEditingIndex(null); // fin de edición/alta
      } else if (action === 'start') {
        setViewMode('flow');
      }
      // Al confirmar el carrito, se vacía el espejo local (la reserva ya se creó). Si el servidor
      // rechaza (ShowError: faltan datos, pago online no disponible…) NO se creó nada: se conserva el
      // carrito y se muestra el motivo en "Revisa y confirma".
      if (action === 'confirm_cart') {
        if (response.uiIntent.type === 'ShowError') {
          setCartConfirmError(String((response.uiIntent.data as any)?.message || tLng('booking.error_title')));
          // Se distingue por código, nunca por el texto del mensaje.
          if ((response.uiIntent.data as any)?.code === 'missing_contact') setCartContactRequired(true);
        } else {
          setCartConfirmError(null);
          setCartContactRequired(false);
          setCartSummary(null);
          setViewMode('flow');
          try { sessionStorage.removeItem(`wp_cart_${effectiveTenantId}`); } catch { /* ignore */ }
        }
      } else {
        setCartConfirmError(null);
      }

      if (!isBackNavigationRef.current && response.uiIntent.type !== 'Redirect' && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        params.set('step', response.uiIntent.type);
        window.history.pushState({ step: response.uiIntent.type }, '', `${window.location.pathname}?${params}`);
      }
      isBackNavigationRef.current = false;

      // Si es redirección (solo cuando no es embedded: Telegram o fallback web), navegar
      if (response.uiIntent.type === 'Redirect') {
        navigate(response.uiIntent.data.redirect.url);
      }
      // EmbeddedCheckout: no redirigir; se muestra el modal en la misma página (solo web)
    } catch (err: any) {
      setError(resolveBookingErrorMessage(err, tLng));
      isBackNavigationRef.current = false;
    } finally {
      if (useGlobalLoading) {
        setLoading(false);
      }
    }
  };

  handleActionRef.current = handleAction;

  const rescheduleBookingIdParam = (searchParams.get('reschedule_booking_id') || '').trim();

  useEffect(() => {
    if (!sessionId || loading || error || !rescheduleBookingIdParam || rescheduleDeepLinkDoneRef.current) {
      return;
    }
    rescheduleDeepLinkDoneRef.current = true;
    void handleActionRef.current('reschedule_booking', { bookingId: rescheduleBookingIdParam });
  }, [sessionId, loading, error, rescheduleBookingIdParam]);

  useEffect(() => {
    const onPopState = () => {
      const step = currentStepRef.current;
      const backAction = step ? STEP_TO_BACK_ACTION[step] : '';
      if (backAction && sessionId) {
        isBackNavigationRef.current = true;
        handleActionRef.current(backAction, {});
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [sessionId]);

  // When a date has no availability the backend returns a ShowError intent, but we re-render
  // the date carousel inline (see the 'ShowError' case) so the user can pick another date.
  // Treat that as the SelectDate step so the "Volver" button stays visible and back navigation
  // (via popstate → currentStepRef) still works instead of silently doing nothing.
  const showErrorAsDateCarousel =
    uiIntent?.type === 'ShowError' && calendarDatesRef.current.length > 0;
  const logicalStep = showErrorAsDateCarousel ? 'SelectDate' : (uiIntent?.type ?? null);
  currentStepRef.current = logicalStep;

  const handleBack = () => {
    if (logicalStep && STEP_TO_BACK_ACTION[logicalStep] && sessionId) {
      window.history.back();
    }
  };

  const canGoBack = !!(logicalStep && STEP_TO_BACK_ACTION[logicalStep]);

  // SPA navigation to "My bookings" that preserves the channel identity so the user is not
  // lost after confirming. Uses react-router navigate() instead of window.location.replace()
  // (a hard reload to an origin-absolute /bookings can 404 under a base-path/subdomain setup).
  // The 'default-tenant' sentinel is omitted so BookingsPage applies its own fallback rather
  // than receiving an invalid tenant id.
  const navigateToBookings = useCallback(() => {
    const params = new URLSearchParams();
    if (tenantId && tenantId !== 'default-tenant') params.set('tenant_id', tenantId);
    if (telegramId) params.set('telegram_id', telegramId);
    if (whatsappId) params.set('whatsapp_id', whatsappId);
    if (waBookingToken) params.set('wa_booking_token', waBookingToken);
    if (emailForBookings) params.set('email', emailForBookings);
    const qs = params.toString();
    navigate(`/bookings${qs ? `?${qs}` : ''}`);
  }, [tenantId, telegramId, whatsappId, waBookingToken, emailForBookings, navigate]);

  // El rediseño de 3 pasos suprime la pantalla "Resumen": no aporta ninguna decisión nueva porque
  // repite lo que el cliente acaba de elegir y lo vuelve a repetir la pantalla de revisar y pagar.
  // Al llegar ConfirmBooking desde el flujo web se añade la línea a la reserva y se salta directo
  // al paso 3. Se conserva la pantalla en los dos casos donde SÍ decide algo: proponer un cambio de
  // hora (reschedule) y el flujo de restaurante, que tiene su propia UI.
  const autoAddedConfirmKeyRef = useRef('');
  useEffect(() => {
    if (uiIntent?.type !== 'ConfirmBooking' || !sessionId || loading) return;
    if (catalogScope === 'restaurant') return;
    if ((uiIntent as any)?.metadata?.is_reschedule === true) return;
    const b = uiIntent.data?.booking as { slot?: string; date?: string; time?: string } | undefined;
    const key = `${sessionId}:${b?.date ?? ''}:${b?.time ?? ''}:${b?.slot ?? ''}:${editingIndex ?? 'new'}`;
    if (autoAddedConfirmKeyRef.current === key) return;
    autoAddedConfirmKeyRef.current = key;
    void handleActionRef.current(
      'add_service',
      editingIndex != null ? { replaceIndex: editingIndex } : {},
    );
  }, [uiIntent, sessionId, loading, catalogScope, editingIndex]);

  // Auto-select the first available date when SelectDate arrives so the user lands
  // directly on the combined date+slot view without having to tap first.
  const lastAutoSelectKeyRef = useRef('');
  useEffect(() => {
    if (uiIntent?.type !== 'SelectDate' || !sessionId) return;
    const dates: DateOption[] = uiIntent.data?.dates || [];
    const firstAvailable = dates.find((d) => d.available !== false);
    if (!firstAvailable) return;
    const key = `${sessionId}:${firstAvailable.date}`;
    if (lastAutoSelectKeyRef.current === key) return;
    lastAutoSelectKeyRef.current = key;
    calendarSelectedDateRef.current = firstAvailable.date;
    void handleActionRef.current('select_date', { date: firstAvailable.date });
  }, [uiIntent, sessionId]);

  useEffect(() => {
    if (!uiIntent?.type) return;
    // Reset tras montar el contenido del nuevo paso (AnimatePresence monta en el siguiente frame)
    // y desactivando cualquier re-anclaje de scroll del navegador. Cubrimos window + documento.
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      if (typeof document !== 'undefined') {
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [uiIntent?.type]);

  // Superpone el estado "sin horarios" (gris) sobre las fechas del backend, sin mutarlas.
  const applyNoSlotDates = (dates: DateOption[]): DateOption[] =>
    noSlotDatesRef.current.size === 0
      ? dates
      : dates.map((d) =>
          noSlotDatesRef.current.has(d.date.slice(0, 10)) ? { ...d, available: false } : d,
        );

  /**
   * Props del filtro "¿con quién?" compartidas por los tres puntos donde se pinta el paso 2
   * (SelectDate, SelectSlot y el ShowError que conserva el carrusel).
   */
  const resourceFilterProps = () => ({
    resourceOptions: calendarResourcesRef.current,
    selectedResourceId: calendarResourceIdRef.current,
    onResourceFilter: (resourceId: string | null) => {
      calendarResourceIdRef.current = resourceId;
      void handleAction('set_resource_filter', { resourceId });
    },
  });

  /**
   * Confirmar hueco. En el flujo web la pregunta del profesional SIEMPRE queda resuelta en el paso
   * 2 (chips de "¿con quién?" o "Cualquiera"), así que `autoResource` va siempre: le dice al backend
   * que no abra una pantalla más.
   *
   * Se manda incondicionalmente a propósito. Condicionarlo a un ref del cliente creaba dos fuentes
   * de verdad —el ref y el `resourceId` de la sesión— que se desincronizaban al reeditar una línea
   * o cuando el backend no enviaba chips, y reaparecía la pantalla de profesional. Cuando el cliente
   * sí eligió a alguien, el `resourceId` ya está en la sesión y el backend ni mira este flag.
   */
  const selectSlot = (slotId: string, timeString?: string) =>
    handleAction('select_slot', {
      slotId,
      ...(timeString ? { timeString } : {}),
      autoResource: true,
    });

  const renderContent = () => {
    if (tenantFromHost.loading) {
      return (
        <div className="booking-loading">
          <div className="spinner"></div>
          <p>{t('booking.loading')}</p>
        </div>
      );
    }

    // Solo mostrar error de tenant si no hay flujo ya cargado (evita que "desaparezcan" los servicios)
    if (tenantFromHost.error && !tenantFromHost.tenantId && !uiIntent) {
      return (
        <div className="booking-error">
          <h2>{t('booking.error_title')}</h2>
          <p>{tenantFromHost.error}</p>
        </div>
      );
    }

    // Pantalla completa de carga SOLO en el arranque (aún no hay paso que mostrar).
    // Durante un avance de paso NO desmontamos el contenido: mantenemos el paso actual
    // montado y mostramos un overlay sutil (clase `is-loading`, barra de progreso fina),
    // de modo que la altura del contenedor nunca colapse ni salte.
    if (loading && !uiIntent) {
      return (
        <div className="booking-loading">
          <div className="spinner"></div>
          <p>{t('booking.loading')}</p>
        </div>
      );
    }

    if (error) {
      const isIdentityError = error.includes('identity_resolution_failed') || error.includes('503');
      const errorMessage = isIdentityError ? tLng('booking.error_identity_failed') : error;
      return (
        <div className="booking-error">
          <h2>{t('booking.error_title')}</h2>
          <p>{errorMessage}</p>
          <button onClick={() => void initializeBooking()}>{t('booking.retry')}</button>
        </div>
      );
    }

    if (!uiIntent) {
      return (
        <div className="booking-loading">
          <div className="spinner"></div>
          <p>{t('booking.loading')}</p>
          <button onClick={() => void initializeBooking()}>{t('booking.retry')}</button>
        </div>
      );
    }

    // Reserva viva: en modo 'draft' (no restaurant) la pantalla principal es "Tu reserva" — lista
    // editable de líneas — con la barra persistente. Los pasos de selección son sub-flujos ('flow').
    if (viewMode === 'draft' && catalogScope !== 'restaurant' && (cartSummary?.items?.length ?? 0) > 0) {
      return (
        <div className={`booking-step-container${loading ? ' is-loading' : ''}`} style={{ paddingBottom: 96 }}>
          <CartReview
            cart={cartSummary!}
            language={language}
            serverError={cartConfirmError}
            hasIdentity={(isSignedIn || !!userId || isChannelIdentityFlow) && !cartContactRequired}
            initialContact={clerkContactDefaults}
            onRemoveLine={(index) => handleAction('remove_service', { index })}
            onAddAnother={() => handleAction('start', {})}
            onConfirm={(contact, paymentMode, bonoLineIndexes) => handleAction('confirm_cart', { ...(contact ? { contactInfo: contact } : {}), ...(paymentMode ? { paymentMode } : {}), ...(bonoLineIndexes && bonoLineIndexes.length ? { bonoLineIndexes } : {}) })}
            onBuyBundle={() => void handleAction('view_my_bundles', {})}
            onEditLine={(index, line) => {
              // Reeditar en sitio: NO se quita la línea; se re-entra a su sub-flujo y al terminar se
              // sustituye (replaceIndex). Si el usuario abandona, la línea original permanece.
              setEditingIndex(index);
              setViewMode('flow');
              void handleAction('select_service', { serviceId: line.serviceId });
            }}
          />
          <CartBottomBar
            cart={cartSummary!}
            language={language}
            hidePrimary
            primaryLabel=""
            onPrimary={() => {}}
          />
        </div>
      );
    }

    const isRestaurantLiveIntent =
      catalogScope === 'restaurant' &&
      ['SelectService', 'ShowForm', 'SelectDate', 'SelectSlot', 'ConfirmBooking', 'ShowError'].includes(uiIntent.type);
    if (isRestaurantLiveIntent) {
      return (
        <RestaurantLiveBooking
          uiIntent={uiIntent}
          loading={loading}
          language={language}
          onAction={(action, payload) => handleAction(action, payload)}
          t={(key, opts) => t(key, { lng: language, ...(opts || {}) })}
        />
      );
    }

    let stepContent: ReactNode;
    switch (uiIntent.type) {
      case 'SelectService': {
        // Volver al catálogo invalida el filtro de profesional: los recursos son por servicio.
        calendarResourcesRef.current = [];
        calendarResourceIdRef.current = null;
        const teaser = (uiIntent.data as any)?.bundleTeaser as
          | { name?: string; sessionCount: number; price: number; currency: string; validityDays: number }
          | undefined;
        stepContent = (
          <ServiceSelection
            services={uiIntent.data.services || []}
            onSelect={(serviceId) => handleAction('select_service', { serviceId })}
            language={language}
            i18n={uiIntent.data.i18n}
            bundleTeaser={
              teaser && teaser.sessionCount > 0
                ? {
                    badge: String(teaser.sessionCount),
                    label: `${teaser.name || tLng('booking.bundle_label')} · ${formatCurrency(teaser.price, teaser.currency, lng)}`,
                    hint: teaser.validityDays
                      ? `${tLng('booking.bundle_validity_days', { days: teaser.validityDays })} · ${tLng('booking.bundle_teaser_hint')}`
                      : tLng('booking.bundle_teaser_hint'),
                  }
                : null
            }
            onOpenBundles={() => void handleAction('view_my_bundles', {})}
          />
        );
        break;
      }
      case 'ShowForm': {
        const formData = (uiIntent.data?.form || {}) as {
          title?: string;
          fields?: Array<{ id: string; label: string; type?: string; required?: boolean; placeholder?: string; options?: Array<{ value: string; label: string }> }>;
        };
        const formMeta = ((uiIntent as any)?.metadata || {}) as { minPartySize?: number; maxPartySize?: number };
        const submitAction = uiIntent.actions?.[0]?.id || 'confirm';
        const fields = Array.isArray(formData.fields) ? formData.fields : [];
        stepContent = (
          <RestaurantDetailsForm
            title={formData.title}
            message={uiIntent.data?.message}
            fields={fields}
            minPartySize={formMeta.minPartySize ?? 1}
            maxPartySize={formMeta.maxPartySize ?? 20}
            submitLabel={uiIntent.actions?.[0]?.label || t('booking.continue')}
            onSubmit={(payload) => void handleAction(submitAction, payload)}
          />
        );
        break;
      }
      case 'SelectResource':
        stepContent = (
          <ResourceSelection
            resources={uiIntent.data.resources || []}
            service={uiIntent.data.service}
            date={uiIntent.data.resourceDate}
            formattedDate={uiIntent.data.resourceFormattedDate}
            time={uiIntent.data.resourceTime}
            allowAutoAssign={uiIntent.data.allowAutoAssign}
            onSelect={(resourceId) => handleAction('select_resource', { resourceId })}
            onAutoAssign={() => handleAction('select_resource', { resourceId: null })}
            language={language}
            i18n={uiIntent.data.i18n}
          />
        );
        break;
      case 'SelectDate': {
        const dates: DateOption[] = uiIntent.data.dates || [];
        const svc = uiIntent.data.service || uiIntent.data.selectedService || null;
        calendarDatesRef.current = dates;
        calendarSelectedDateRef.current = null;
        calendarServiceRef.current = svc;
        stepContent = (
          <DateSlotSelection
            dates={applyNoSlotDates(dates)}
            selectedDate={null}
            timeSlots={null}
            loadingSlots={false}
            onDateSelect={(date) => {
              calendarSelectedDateRef.current = date;
              handleAction('select_date', { date });
            }}
            onSlotSelect={selectSlot}
            language={language}
            service={svc}
            {...resourceFilterProps()}
          />
        );
        break;
      }
      case 'SelectSlot':
        if (uiIntent.data.slotDate && uiIntent.data.timeSlots) {
          const requestedPartySize =
            typeof ((uiIntent as any)?.metadata?.partySize) === 'number'
              ? Number((uiIntent as any).metadata.partySize)
              : undefined;
          calendarSelectedDateRef.current = uiIntent.data.slotDate;
          // If we have no persisted dates (e.g. deep-link), create a single-entry fallback
          if (calendarDatesRef.current.length === 0) {
            calendarDatesRef.current = [{ date: uiIntent.data.slotDate, formatted: '', available: true }];
          }
          // Los chips solo viajan con los huecos; se guardan para los renders que no los traen.
          if (Array.isArray(uiIntent.data.resourceOptions)) {
            calendarResourcesRef.current = uiIntent.data.resourceOptions;
            calendarResourceIdRef.current = uiIntent.data.selectedResourceId ?? null;
          }
          stepContent = (
            <DateSlotSelection
              dates={applyNoSlotDates(calendarDatesRef.current)}
              selectedDate={uiIntent.data.slotDate}
              timeSlots={uiIntent.data.timeSlots}
              loadingSlots={false}
              onDateSelect={(date) => {
                calendarSelectedDateRef.current = date;
                handleAction('select_date', { date });
              }}
              onSlotSelect={selectSlot}
              language={language}
              service={calendarServiceRef.current}
              requestedPartySize={requestedPartySize}
              {...resourceFilterProps()}
            />
          );
        } else {
          stepContent = (
            <SlotSelection
              slots={uiIntent.data.slots || []}
              onSelect={(slotId) => selectSlot(slotId)}
              language={language}
              i18n={uiIntent.data.i18n}
            />
          );
        }
        break;
      case 'RequestIdentity': {
        if (import.meta.env.DEV) {
          logger.debug('🔍 BookingPage - RequestIdentity uiIntent:', {
            type: uiIntent.type,
            data: uiIntent.data,
            identityOptions: uiIntent.data?.identityOptions,
            allowClerkLogin: uiIntent.data?.allowClerkLogin,
            allowGoogleLogin: uiIntent.data?.allowGoogleLogin,
          });
        }
        stepContent = (
          <IdentityForm
            fields={uiIntent.data.identityFields || []}
            allowGoogleLogin={uiIntent.data.allowGoogleLogin}
            allowClerkLogin={uiIntent.data.allowClerkLogin}
            message={uiIntent.data.identityMessage}
            identityOptions={uiIntent.data.identityOptions} // ✅ NUEVO: Opciones de login/registro o invitado
            onSubmit={(data) => {
              logger.debug('📤 IdentityForm - Enviando datos al confirmar:', data);
              handleAction('confirm', data);
            }}
            onLogin={() => {
              // ✅ Guardar estado de sesión antes de redirigir a Clerk
              if (sessionId && tenantId) {
                localStorage.setItem('booking_sessionId', sessionId);
                localStorage.setItem('booking_tenantId', tenantId);
                localStorage.setItem('booking_pendingAction', 'confirm');
                // Guardar el payload actual si existe (datos del formulario de invitado)
                const currentPayload = uiIntent?.data?.identityOptions ? {} : {};
                localStorage.setItem('booking_pendingPayload', JSON.stringify(currentPayload));
              }
              
              // ✅ Redirigir a Clerk sign-in
              const currentUrl = window.location.href;
              const clerkSignInUrl = `${window.location.origin}/sign-in?redirect_url=${encodeURIComponent(currentUrl)}`;
              window.location.href = clerkSignInUrl;
            }}
          />
        );
        break;
      }
      case 'ConfirmBooking':
        const bookingWithRestaurantMeta = {
          ...uiIntent.data.booking,
          ...(typeof (uiIntent as any)?.metadata?.party_size === 'number'
            ? { party_size: Number((uiIntent as any).metadata.party_size) }
            : {}),
          ...(typeof (uiIntent as any)?.metadata?.preferred_zone === 'string'
            ? { preferred_zone: String((uiIntent as any).metadata.preferred_zone) }
            : {}),
          ...(typeof (uiIntent as any)?.metadata?.remaining_capacity_estimate === 'number'
            ? {
                remaining_capacity_estimate: Number(
                  (uiIntent as any).metadata.remaining_capacity_estimate,
                ),
              }
            : {}),
        };
        // Reschedule (cliente sugiere cambio de hora): NO entra al carrito. El CTA confirma el
        // cambio → handleAction('confirm') → booking-module.proposeReschedule deja la reserva
        // pendiente de aceptación en consola (con su notificación). Se detecta por metadata.
        const isRescheduleReview = (uiIntent as any)?.metadata?.is_reschedule === true;
        // El efecto de arriba ya está añadiendo esta selección a la reserva: mostrar el resumen
        // durante ese instante sería un parpadeo de una pantalla que hemos decidido eliminar.
        if (!isRescheduleReview && catalogScope !== 'restaurant') {
          stepContent = (
            <div className="booking-loading">
              <div className="spinner"></div>
              <p>{t('booking.loading')}</p>
            </div>
          );
          break;
        }
        // Modo carrito (web, no restaurant): el CTA primario AÑADE la selección a la reserva viva
        // y vuelve a "Tu reserva". El path single-confirm queda para restaurant/legacy.
        stepContent = (
          <BookingReview
            booking={bookingWithRestaurantMeta}
            language={language}
            confirmLabel={
              isRescheduleReview
                ? (language === 'en' ? 'Confirm time change' : 'Confirmar cambio de hora')
                : editingIndex != null
                  ? (language === 'en' ? 'Update booking' : 'Actualizar reserva')
                  : (language === 'en' ? 'Add to booking' : 'Añadir a la reserva')
            }
            onConfirm={() =>
              isRescheduleReview
                ? handleAction('confirm', {})
                : handleAction('add_service', editingIndex != null ? { replaceIndex: editingIndex } : {})
            }
            onCancel={() =>
              isRescheduleReview
                ? handleAction('cancel', {})
                : (cartSummary?.items?.length ?? 0) > 0
                  ? handleAction('view_cart', {})
                  : handleAction('cancel', {})
            }
          />
        );
        break;
      case 'ShowConfirmation': {
        const confData = uiIntent.data as any;
        // Carrito multi-servicio confirmado (gratis): confirmación agregada por número de reservas.
        const cartConf = confData?.cart as
          | {
              id?: string;
              bookings?: number;
              total?: number;
              currency?: string | null;
              paymentMode?: string | null;
              lines?: ConfirmedLineVM[];
            }
          | undefined;
        if (cartConf) {
          stepContent = (
            <BookingConfirmation
              lines={Array.isArray(cartConf.lines) ? cartConf.lines : []}
              total={cartConf.total}
              currency={cartConf.currency}
              paymentMode={cartConf.paymentMode}
              customerEmail={(confData?.customerEmail as string | undefined) ?? emailForBookings}
              language={language}
              tenantId={tenantId}
              t={tLng}
              onSeeBookings={() => navigateToBookings()}
              onBackHome={() => void initializeBooking()}
            />
          );
          break;
        }
        const hasCalendarData = Boolean(confData?.slot_start_iso && confData?.slot_end_iso);
        stepContent = (
          <WebpaymentConfirmation
            moduleType="booking"
            paymentCompleted={false}
            title={confData?.title ? stripEmojiUnicode(String(confData.title)) : undefined}
            description={typeof uiIntent.data.message === 'string' ? stripEmojiUnicode(uiIntent.data.message) : undefined}
            reservation={{
              serviceName: confData?.service_name ? stripEmojiUnicode(String(confData.service_name)) : undefined,
              startIso: confData?.slot_start_iso ?? '',
              endIso: confData?.slot_end_iso,
              resourceName: confData?.resource_name ? stripEmojiUnicode(String(confData.resource_name)) : undefined,
              bookingId: confData?.bookingId,
            }}
            showAddToCalendar={hasCalendarData}
            language={language}
            tenantId={tenantId}
            onPrimaryAction={() => navigateToBookings()}
            onBackToMenu={() => void initializeBooking()}
          />
        );
        break;
      }
      case 'ShowError': {
        // "Sin horarios para esta fecha": conservamos SIEMPRE el carrusel si tenemos fechas, sin
        // depender de calendarSelectedDateRef (el render de SelectDate lo resetea). El día sin
        // horarios ya viene marcado en gris vía applyNoSlotDates (registrado en handleAction),
        // no aquí en render. Mostramos el mensaje inline; el usuario elige otra fecha sin salir.
        if (calendarDatesRef.current.length > 0) {
          calendarSelectedDateRef.current = null;
          stepContent = (
            <DateSlotSelection
              dates={applyNoSlotDates(calendarDatesRef.current)}
              selectedDate={null}
              timeSlots={null}
              loadingSlots={false}
              onDateSelect={(date) => {
                calendarSelectedDateRef.current = date;
                handleAction('select_date', { date });
              }}
              onSlotSelect={selectSlot}
              language={(uiIntent.data as any)?.language || language}
              service={calendarServiceRef.current}
              {...resourceFilterProps()}
              noSlotsMessage={normalizeBackendErrorMessage(
                stripEmojiUnicode(String(uiIntent.data.message || '')),
                tLng,
              )}
            />
          );
        } else {
          stepContent = (
            <div className="booking-error">
              <h2>{tLng('booking.error_title')}</h2>
              <p>{normalizeBackendErrorMessage(stripEmojiUnicode(String(uiIntent.data.message || '')), tLng)}</p>
              <button onClick={() => void initializeBooking()}>{tLng('booking.retry')}</button>
            </div>
          );
        }
        break;
      }
      case 'ShowList': {
        const cartData = (uiIntent.data as any)?.cart as CartSummaryVM | undefined;
        if (cartData) {
          stepContent = (
            <CartReview
              cart={cartData}
              language={language}
              serverError={cartConfirmError}
              hasIdentity={(isSignedIn || !!userId || isChannelIdentityFlow) && !cartContactRequired}
              initialContact={clerkContactDefaults}
              onRemoveLine={(index) => handleAction('remove_service', { index })}
              onAddAnother={() => handleAction('start', {})}
              onConfirm={(contact, paymentMode, bonoLineIndexes) => handleAction('confirm_cart', { ...(contact ? { contactInfo: contact } : {}), ...(paymentMode ? { paymentMode } : {}), ...(bonoLineIndexes && bonoLineIndexes.length ? { bonoLineIndexes } : {}) })}
            onBuyBundle={() => void handleAction('view_my_bundles', {})}
            />
          );
          break;
        }
        const list = uiIntent.data?.list;
        const items = list?.items || [];
        const actions = (uiIntent.actions || []) as Array<{
          id: string;
          label: string;
          payload?: { action?: string; bookingId?: string };
          style?: string;
        }>;
        stepContent = (
          <div className="booking-show-list">
            <pre className="booking-show-list-title" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {stripEmojiUnicode(String(list?.title || ''))}
            </pre>
            <ul className="booking-show-list-items">
              {items.map((item: { id: string; title?: string; subtitle?: string; description?: string }) => {
                const rowActions = actions.filter((a) => a.payload?.bookingId === item.id);
                return (
                  <li key={item.id} className="booking-show-list-row">
                    <div className="booking-show-list-row-text">
                      <div className="booking-show-list-item-title">
                        {item.title ? stripEmojiUnicode(item.title) : ''}
                      </div>
                      {item.subtitle ? (
                        <div className="booking-show-list-item-sub">{stripEmojiUnicode(item.subtitle)}</div>
                      ) : null}
                      {item.description ? (
                        <div className="booking-show-list-item-desc">{stripEmojiUnicode(item.description)}</div>
                      ) : null}
                    </div>
                    {rowActions.length > 0 ? (
                      <div className="booking-show-list-row-actions">
                        {rowActions.map((act) => (
                          <button
                            key={act.id}
                            type="button"
                            className={
                              act.style === 'danger' ? 'booking-menu-item-btn booking-menu-item-btn--danger' : 'booking-menu-item-btn'
                            }
                            onClick={() => {
                              const a = act.payload?.action;
                              const bid = act.payload?.bookingId;
                              if (a && bid) void handleAction(a, { bookingId: bid });
                            }}
                          >
                            {stripEmojiUnicode(act.label)}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
        break;
      }
      case 'ShowMenu': {
        const menu = uiIntent.data?.menu;
        const items = menu?.items || [];
        const isMyBundles = uiIntent.data?.viewType === 'my_bundles';
        if (isMyBundles) {
          const myBundles: Array<{
            id: string; sessionsTotal: number; sessionsConsumed: number;
            sessionsRemaining: number; expiresAt: string; status: string;
          }> = Array.isArray(uiIntent.data.myBundles) ? uiIntent.data.myBundles : [];
          const availableBundles: Array<{
            id: string; name: string; session_count: number; validity_days: number;
            price: number; currency: string; payment_mode: string; description?: string;
          }> = Array.isArray(uiIntent.data.availableBundles) ? uiIntent.data.availableBundles : [];
          stepContent = (
            <BundlePurchase
              myBundles={myBundles}
              availableBundles={availableBundles}
              language={language}
              t={tLng}
              onPurchase={(b) =>
                void handleAction('purchase_bundle', {
                  bundleId: b.id,
                  bundleName: b.name,
                  price: b.price,
                  currency: b.currency,
                  sessionCount: b.session_count,
                  validityDays: b.validity_days,
                  paymentMode: b.payment_mode,
                  embedded: true,
                  origin: typeof window !== 'undefined' ? window.location.origin : '',
                })
              }
              onBack={() => void handleAction('start', {})}
            />
          );
          break;
        }
        const isPaymentMode = items.some((i: any) => i.id?.startsWith?.('payment_mode:'));
        if (isPaymentMode && items.length >= 2) {
          stepContent = (
            <div className="booking-payment-mode">
              <h2>{t('booking.how_to_pay')}</h2>
              <p>{stripEmojiUnicode(String(uiIntent.data.message || t('booking.choose_option')))}</p>
              <div className="payment-mode-buttons">
                <button
                  className="payment-mode-btn prepaid"
                  onClick={() => handleAction('confirm', { paymentMode: 'prepaid' })}
                >
                  {tLng('booking.pay_prepaid')}
                </button>
                <button
                  className="payment-mode-btn postpaid"
                  onClick={() => handleAction('confirm', { paymentMode: 'postpaid' })}
                >
                  {tLng('booking.pay_on_site')}
                </button>
              </div>
              <button className="back-btn" onClick={() => handleAction('cancel', {})}>{t('booking.cancel')}</button>
            </div>
          );
        } else {
          const title = stripEmojiUnicode((menu?.title || '').replace(/\n+/g, ' ').trim());
          const menuMsg =
            typeof uiIntent.data.message === 'string' ? stripEmojiUnicode(uiIntent.data.message) : '';
          stepContent = (
            <div className="booking-menu booking-menu--generic">
              {title ? <h2 className="booking-menu-generic-title">{title}</h2> : null}
              {menuMsg ? <p className="booking-menu-generic-msg">{menuMsg}</p> : null}
              <div className="booking-menu-generic-items">
                {items.map((item: { id: string; label?: string; description?: string }) => (
                  <button
                    key={item.id}
                    type="button"
                    className="booking-menu-item-btn"
                    onClick={() => {
                      if (item.id === 'back') {
                        void handleAction('view_bookings', {});
                      } else {
                        void handleAction(item.id, {});
                      }
                    }}
                  >
                    <span className="booking-menu-item-label">
                      {item.label ? stripEmojiUnicode(item.label) : ''}
                    </span>
                    {item.description ? (
                      <span className="booking-menu-item-desc">
                        {stripEmojiUnicode(item.description)}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          );
        }
        break;
      }
      case 'EmbeddedCheckout': {
        // Solo web: pago en modal (Telegram sigue con Redirect/enlace). Clave Stripe: env o API del gateway.
        const clientSecret = uiIntent.data?.clientSecret;
        if (!clientSecret) {
          stepContent = (
            <div className="booking-error">
              <h2>{tLng('booking.error_title')}</h2>
              <p>{tLng('booking.checkout_load_error')}</p>
              <button onClick={() => void initializeBooking()}>{tLng('booking.back_to_start')}</button>
            </div>
          );
        } else if (
          stripeKeyLoading ||
          (getPaymentGatewayBrowserBase() && tenantId && !effectiveStripeKey && !stripeKeyFetchFailed && !stripeKeyEmptyFromApi)
        ) {
          stepContent = (
            <div className="booking-loading">
              <div className="spinner"></div>
              <p>{t('booking.loading')}</p>
            </div>
          );
        } else if (!effectiveStripeKey || !stripePromise) {
          const stripeHelp =
            !STRIPE_PUBLISHABLE_KEY && !getPaymentGatewayPublicUrl()
              ? tLng('booking.stripe_error_no_gateway')
              : stripeKeyFetchFailed
                ? tLng('booking.stripe_error_fetch')
                : tLng('booking.stripe_error_no_pk_tenant');
          stepContent = (
            <div className="booking-error">
              <h2>{tLng('booking.error_title')}</h2>
              <p>{stripeHelp}</p>
              <button onClick={() => void initializeBooking()}>{tLng('booking.back_to_start')}</button>
            </div>
          );
        } else {
          stepContent = (
            <div className="booking-embedded-checkout" style={{ maxWidth: 900, margin: '0 auto' }}>
              <UnifiedModulePaymentCheckout
                stripePromise={stripePromise}
                embeddedClientSecret={clientSecret}
                embeddedCompletionNavigate={
                  (uiIntent.data as { embeddedCompletion?: { path: string; paymentIntentId: string; tenantId: string } })
                    ?.embeddedCompletion ?? null
                }
                embeddedHeader={
                  <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="booking-back-btn"
                      // Re-init the session to clear the EmbeddedCheckout state. A plain
                      // navigate() to the same /booking route leaves this component mounted
                      // with uiIntent still === 'EmbeddedCheckout', so the checkout would stay
                      // on screen and the button would appear to do nothing.
                      onClick={() => void initializeBooking()}
                    >
                      {t('booking.cancel')}
                    </button>
                  </div>
                }
              />
            </div>
          );
        }
        break;
      }
      default:
        stepContent = (
          <div className="booking-unknown">
            <p>{tLng('payment.flow.unknown_ui_step', { type: uiIntent.type })}</p>
            <pre>{JSON.stringify(uiIntent, null, 2)}</pre>
          </div>
        );
    }

    const onCartScreen = uiIntent?.type === 'ShowList' && !!(uiIntent?.data as any)?.cart;
    // El carrito solo aparece cuando ya hay al menos un servicio en la reserva (estilo Fresha):
    // hasta entonces no mostramos la barra. Se oculta también en pantallas terminales (pago/confirmación).
    const isTerminal = uiIntent?.type === 'Redirect' || uiIntent?.type === 'ShowConfirmation';
    const barCart = cartSummary ?? ({ items: [], totalAmount: 0 } as CartSummaryVM);
    const barCount = barCart.count ?? barCart.items?.length ?? 0;
    // El paso 2 trae su propia barra inferior (hueco elegido + Continuar): dos barras fijas se
    // solaparían y competirían por la misma decisión.
    // La pantalla de bonos vive dentro de /booking pero es una compra aparte, con su propio CTA:
    // la barra fija del carrito se le montaba encima y ofrecía "Ver reserva" mientras el usuario
    // está eligiendo un pack.
    const onBundleScreen = uiIntent?.type === 'ShowMenu' && (uiIntent?.data as any)?.viewType === 'my_bundles';
    const stepOwnsBottomBar =
      uiIntent?.type === 'SelectSlot' || uiIntent?.type === 'SelectDate' || onBundleScreen;
    const showCartBar = !isTerminal && !stepOwnsBottomBar && catalogScope !== 'restaurant' && barCount > 0;
    // Reservamos el espacio de la barra durante todo el flujo de booking (no solo cuando ya hay
    // servicios), para que al añadir el primer servicio la barra no empuje el contenido de golpe.
    const reserveCartSpace = !isTerminal && catalogScope !== 'restaurant';
    return (
      <div
        className={`booking-step-container${loading ? ' is-loading' : ''}`}
        style={reserveCartSpace ? { paddingBottom: 96 } : undefined}
      >
        {canGoBack && (
          <button type="button" className="booking-back-btn" onClick={handleBack} aria-label={t('booking.back')}>
            ← {t('booking.back')}
          </button>
        )}
        <StepTransition stepKey={uiIntent.type}>{stepContent}</StepTransition>
        {showCartBar ? (
          <CartBottomBar
            cart={barCart}
            language={language}
            hidePrimary={onCartScreen}
            primaryLabel={
              language === 'en' ? `View booking (${barCount})` : `Ver reserva (${barCount})`
            }
            onPrimary={() => handleAction('view_cart', {})}
          />
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  /*
   * El idioma de la sesión del orquestador solo SIEMBRA el local, nunca lo pisa. Antes los
   * componentes de paso recibían `uiIntent.data.language || language`, así que mandaba el
   * backend: el selector marcaba ES (estado local) mientras la pantalla se pintaba en el idioma
   * de la sesión, normalmente EN. Se adopta solo si el usuario no ha elegido nunca —enlaces con
   * ?lang= siguen funcionando— y a partir de ahí manda su elección, como ya hacía GymPage.
   */
  useEffect(() => {
    const fromSession = (uiIntent?.data as { language?: string } | undefined)?.language;
    if (!fromSession || !['es', 'en', 'fr', 'de'].includes(fromSession)) return;
    let chosen: string | null = null;
    try {
      chosen = localStorage.getItem('booking_language');
    } catch {
      // almacenamiento no disponible: se trata como "sin elección previa"
    }
    if (chosen) return;
    setLanguage((prev) => (prev === fromSession ? prev : fromSession));
  }, [uiIntent]);

  // En pantallas finales usar siempre el idioma seleccionado (evita desfase por changeLanguage asíncrono)
  const tLng = (key: string, options?: Record<string, string | number>) =>
    t(key, {
      lng: ['es', 'en', 'fr', 'de'].includes(language) ? language : 'es',
      ...options,
    });

  const lng = ['es', 'en', 'fr', 'de'].includes(language) ? language : 'es';

  useEffect(() => {
    const d = uiIntent?.data as Record<string, unknown> | undefined;
    const flash = d?.scheduleFlash as { kind?: string; message?: string } | undefined;

    if (flash?.message) {
      const k =
        flash.kind === 'error' ? 'error' : flash.kind === 'info' ? 'info' : 'success';
      setFlowFlashLocal({ kind: k, message: flash.message });
      const id = window.setTimeout(() => setFlowFlashLocal(null), 7000);
      return () => window.clearTimeout(id);
    }

    if (uiIntent?.type === 'ShowConfirmation') {
      const title = typeof d?.title === 'string' ? d.title.trim() : '';
      const message = typeof d?.message === 'string' ? d.message.trim() : '';
      const body =
        title && message && title !== message
          ? `${title}\n${message}`
          : message || title || t('booking.confirm_message_default', { lng });
      setFlowFlashLocal({ kind: 'success', message: body });
      const id = window.setTimeout(() => setFlowFlashLocal(null), 7000);
      return () => window.clearTimeout(id);
    }

    if (uiIntent?.type === 'ShowError') {
      // Si conservamos el carrusel (hay fechas), el mensaje ya se muestra inline en el área de
      // slots: no lanzamos el toast de error para no duplicar.
      if (calendarDatesRef.current.length > 0) {
        setFlowFlashLocal(null);
        return undefined;
      }
      const message = normalizeBackendErrorMessage(
        typeof d?.message === 'string' ? d.message : '',
        (key) => t(key, { lng }),
      );
      setFlowFlashLocal({ kind: 'error', message });
      const id = window.setTimeout(() => setFlowFlashLocal(null), 7000);
      return () => window.clearTimeout(id);
    }

    setFlowFlashLocal(null);
    return undefined;
  }, [uiIntent, lng, t]);

  /**
   * Cabecera contextual de los pasos intermedios. En el paso 1 y en la confirmación la barra
   * identifica al negocio (y lleva idioma y cuenta); en el 2 y el 3 dice de qué estamos hablando,
   * que es lo único que el cliente necesita saber ahí.
   */
  const lockMeta = (
    <span className="booking-header-context-secure">
      <Lock size={12} strokeWidth={2.2} aria-hidden />
      {tLng('booking.secure_payment')}
    </span>
  );
  const stepContextBar = (() => {
    if (catalogScope === 'restaurant' || paymentReturn?.paymentIntentId) return null;
    const step = uiIntent?.type;
    const onCartScreen =
      (viewMode === 'draft' && (cartSummary?.items?.length ?? 0) > 0) ||
      (step === 'ShowList' && !!(uiIntent?.data as any)?.cart);
    if (onCartScreen) {
      return { title: tLng('booking.your_booking'), meta: lockMeta, onBack: () => handleAction('start', {}) };
    }
    if (step === 'SelectSlot' || step === 'SelectDate') {
      const svc = calendarServiceRef.current;
      if (!svc?.name) return null;
      return {
        title: svc.name,
        meta: serviceMetaLabel(svc, lng),
        onBack: canGoBack ? handleBack : undefined,
      };
    }
    if (step === 'ShowMenu' && uiIntent?.data?.viewType === 'my_bundles') {
      return { title: tLng('booking.bundles_title'), meta: lockMeta, onBack: () => handleAction('start', {}) };
    }
    return null;
  })();

  const onLanguageChange = (newLang: string) => {
    setLanguage(newLang);
    localStorage.setItem('booking_language', newLang);
    i18n.changeLanguage(newLang);
    if (sessionId) {
      handleAction('select_language', { language: newLang });
    } else {
      initializeBooking(newLang);
    }
  };

  return (
    <div className="booking-page">
      {/* `booking-shell` activa el rediseño de 3 pasos. El flujo de restaurante tiene su propia
          pantalla en vivo (RestaurantLiveBooking) y se queda con el chrome anterior. */}
      <div className={`booking-container${catalogScope === 'restaurant' ? '' : ' booking-shell'}`}>
        <BookingFlowHeader
          pageTitle={null}
          contextBar={stepContextBar}
          actions={
            <BookingChromeActions
              language={language}
              onLanguageChange={onLanguageChange}
              t={t}
              tenantId={tenantId}
              emailForBookings={emailForBookings}
              telegramId={telegramId}
              whatsappId={whatsappId}
              onNavigateBookings={() => navigateToBookings()}
              onMyBundles={() => void handleActionRef.current('view_my_bundles', {})}
              activityOpen={activityPanelOpen}
              onActivityOpen={() => setActivityPanelOpen(true)}
              onActivityClose={() => setActivityPanelOpen(false)}
              activityUnreadCount={activityUnreadCount}
              onActivityUnreadCount={setActivityUnreadCount}
              showSignIn={Boolean(CLERK_PUBLISHABLE_KEY) && !isSignedIn && !isChannelIdentityFlow}
              onSignIn={() => {
                const u = typeof window !== 'undefined' ? window.location.href : basePath;
                navigate(`/sign-in?redirect_url=${encodeURIComponent(u)}`);
              }}
              showSignOut={Boolean(CLERK_PUBLISHABLE_KEY) && isSignedIn && Boolean(signOut)}
              onSignOut={signOut}
              onOpenProfile={() => setProfileModalOpen(true)}
            />
          }
        />
        <BookingGuestContactModal
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          tenantId={tenantId}
          initialEmail={isSignedIn && user?.primaryEmailAddress?.emailAddress ? user.primaryEmailAddress.emailAddress : emailForBookings}
          mode={isSignedIn ? 'clerk' : 'guest'}
          whatsappId={whatsappId}
          telegramId={telegramId}
          onSaved={(em) => setGuestEmailLocal(em)}
        />
        {/* "Volver al perfil" solo en la pantalla inicial de servicios. En los pasos 2 y 3 ya hay
            un "← Volver" propio y la marca de la cabecera enlaza al perfil (BookingFlowHeader):
            dos controles de retroceso apilados leían como navegación duplicada. Se mantiene la
            ocultación con reserva en curso en sub-flujo de selección (añadir otro servicio). */}
        {profileSlug && !canGoBack && !((cartSummary?.items?.length ?? 0) > 0 && viewMode === 'flow') && (
          <a href={`/${profileSlug}`} className="booking-back-link">
            <ChevronLeft size={14} strokeWidth={2.5} />
            {t('booking.back_to_profile', 'Volver al perfil')}
          </a>
        )}
        <main className="booking-main">
          {paymentReturn?.paymentIntentId ? (
            <PaymentIntentResultPanel
              paymentIntentId={paymentReturn.paymentIntentId}
              tenantId={tenantId}
              statusFromQuery={paymentReturn.statusCancelled ? 'cancelled' : null}
              variant="embedded"
              moduleBasePath={basePath}
              onDismissEmbedded={() => {
                clearSession();
                setUiIntent(null);
                setSessionId(null);
                initializingRef.current = false;
                try {
                  sessionStorage.removeItem(SESSION_KEY(tenantId));
                  sessionStorage.removeItem(SESSION_TS_KEY(tenantId));
                  sessionStorage.removeItem(SESSION_UI_KEY(tenantId));
                } catch { /* ignore */ }
              }}
            />
          ) : (
            renderContent()
          )}
        </main>
      </div>
      {flowFlashLocal ? (
        <div
          className={`gym-action-toast gym-action-toast--${
            flowFlashLocal.kind === 'info' ? 'info' : flowFlashLocal.kind === 'error' ? 'error' : 'success'
          }`}
          role="status"
        >
          <span className="gym-action-toast-icon" aria-hidden>
            <FlowToastGlyph
              kind={flowFlashLocal.kind === 'error' ? 'error' : flowFlashLocal.kind === 'info' ? 'info' : 'success'}
            />
          </span>
          <div className="gym-action-toast-body">
            <strong className="gym-action-toast-title">
              {flowFlashLocal.kind === 'error' ? tLng('gym.action_error_title') : tLng('gym.action_done_title')}
            </strong>
            <p className="gym-action-toast-msg" style={{ whiteSpace: 'pre-wrap' }}>
              {flowFlashLocal.message}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
